import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import Swal, { SweetAlertIcon } from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  
  // Colores institucionales estándar (Referencia Gob.cl)
  private readonly COLOR_PRIMARY = '#0F69C4'; 
  private readonly COLOR_DANGER = '#D9534F';
  private readonly COLOR_SUCCESS = '#28a745';
  private readonly COLOR_WARNING = '#FFC107';

  constructor(private toastr: ToastrService) { }

  // --- Toasts (Notificaciones breves) ---
  success(mensaje: string) {
    this.toastr.success(
      `<span class="toast-icon"><i class="bi bi-check-circle" style="color:#28a745"></i></span> ${mensaje}`,
      '',
      { enableHtml: true }
    );
  }

  warning(mensaje: string) {
    this.toastr.warning(
      `<span class="toast-icon"><i class="bi bi-exclamation-triangle" style="color:#ffc107"></i></span> ${mensaje}`,
      '',
      { enableHtml: true }
    );
  }

  error(mensaje: string) {
    this.toastr.error(
      `<span class="toast-icon"><i class="bi bi-x-circle" style="color:#dc3545"></i></span> ${mensaje}`,
      '',
      { enableHtml: true }
    );
  }

  // --- Modales Informativos (SweetAlert2) ---

  /**
   * Muestra un modal de éxito
   */
  modalSuccess(titulo: string, mensaje: string) {
    return Swal.fire({
      title: titulo,
      text: mensaje,
      icon: 'success',
      confirmButtonColor: this.COLOR_PRIMARY,
      confirmButtonText: 'Entendido',
      heightAuto: false
    });
  }

  /**
   * Muestra un modal de error
   */
  modalError(titulo: string, mensaje: string) {
    return Swal.fire({
      title: titulo,
      text: mensaje,
      icon: 'error',
      confirmButtonColor: this.COLOR_PRIMARY, // Mantener consistencia en botón de cierre
      confirmButtonText: 'Cerrar',
      heightAuto: false
    });
  }

  /**
   * Muestra un modal de advertencia
   */
  modalWarning(titulo: string, mensaje: string) {
    return Swal.fire({
      title: titulo,
      text: mensaje,
      icon: 'warning',
      confirmButtonColor: this.COLOR_PRIMARY,
      confirmButtonText: 'Aceptar',
      heightAuto: false
    });
  }

  /**
   * Muestra un modal de información general
   */
  modalInfo(titulo: string, mensaje: string) {
    return Swal.fire({
      title: titulo,
      text: mensaje,
      icon: 'info',
      confirmButtonColor: this.COLOR_PRIMARY,
      confirmButtonText: 'Aceptar',
      heightAuto: false
    });
  }

  /**
   * Modal de confirmación (Si/No)
   * Retorna Promise<boolean>: true si confirma, false si cancela
   */
  async confirm(titulo: string, mensaje: string, btnConfirmar: string = 'Confirmar', btnCancelar: string = 'Cancelar'): Promise<boolean> {
    const result = await Swal.fire({
      title: titulo,
      text: mensaje,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: this.COLOR_PRIMARY,
      cancelButtonColor: this.COLOR_DANGER,
      confirmButtonText: btnConfirmar,
      cancelButtonText: btnCancelar,
      reverseButtons: true, // Botón de acción a la derecha es común en UX moderna, pero Gob a veces prefiere izq. Usaremos estándar SWAL invertido para evitar accidentes.
      heightAuto: false
    });

    return result.isConfirmed;
  }

  /**
   * Muestra un modal de carga (Loading)
   */
  showLoading(mensaje: string = 'Cargando...') {
    Swal.fire({
      title: mensaje,
      allowEscapeKey: false,
      allowOutsideClick: false,
      heightAuto: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
  }

  /**
   * Cierra cualquier modal abierto
   */
  close() {
    Swal.close();
  }
}